/**
 * Mojo Up AI Cloud → Team: the people in the active organisation and what each may do, the
 * invitations still open, and inviting more. Owners and admins manage; a team always keeps an
 * owner (the API refuses otherwise, and its sentence is shown). A personal organisation is one
 * person's alone, so it offers a team instead.
 */

import type { CloudBilling, CloudInvitation, CloudMember, CloudRole } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { Copy, CreditCard, Envelope, Persons, Shield } from '@gravity-ui/icons';
import { Button, Dropdown, Input, Label, TextField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, problemText, routes } from '../../api/client.ts';
import { useCloud } from '../../auth/AuthProvider.tsx';
import { BillingBanner, BillingSection, SeatsModal, TrialModal } from '../../components/cloud/Billing.tsx';
import { FormModal } from '../../components/cloud/FormModal.tsx';
import { CreateTeamModal, ROLE_TEXT } from '../../components/cloud/OrganisationSwitcher.tsx';
import { ArtEmpty, ChoiceSelect, ConfirmDialog, ErrorNote, ListBlock, ListGroup, ListRow, Page, PageHeader, Stat } from '../../components/ui.tsx';
import { RowMenu } from '../../components/ui/RowMenu.tsx';
import { VIEW_TONES } from '../../components/ui/Art.tsx';
import { toneFor } from '../../components/ui/ChoiceCard.tsx';
import { useLoad } from '../../hooks.ts';

const tone = VIEW_TONES.team!;
const MANAGERS = new Set<CloudRole>(['owner', 'admin']);
const day = (iso: string) => dateLong(iso, { year: false });
/** Two letters from a name, or the first of an address's local part when there is no name. */
const initials = (m: { name?: string; email?: string }) =>
  m.name
    ? m.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]!.toUpperCase())
        .join('')
    : (m.email?.[0] ?? '?').toUpperCase();

export function TeamPage() {
  const cloud = useCloud()!;
  const org = cloud.organisation;
  // `?create=1` opens the dialog at once: AI Workbench links a free account's "Make a team" here.
  const [creating, setCreating] = useState(() => new URLSearchParams(window.location.search).get('create') === '1');
  if (org.kind === 'personal') {
    return (
      <Page>
        <PageHeader tone={tone} icon={<Persons />} title="Personal" subtitle="Your own space on AI Cloud: your workbenches, projects and settings, seen by nobody else." />
        <ArtEmpty
          tone={tone}
          icon={<Persons />}
          title="Working with other people?"
          action={
            <Button size="sm" variant="primary" onPress={() => setCreating(true)}>
              Create a team
            </Button>
          }
        >
          Your personal space is yours alone, so it has no members. Create a team to invite people and share workbenches, projects, policies and secrets with them.
        </ArtEmpty>
        <CreateTeamModal isOpen={creating} onClose={() => setCreating(false)} />
      </Page>
    );
  }
  return <TeamView key={org.id} />;
}

function TeamView() {
  const cloud = useCloud()!;
  const navigate = useNavigate();
  const org = cloud.organisation;
  const me = cloud.me.id;
  const manager = MANAGERS.has(org.role);
  // Only the owners change billing; an admin reads it.
  const owner = org.role === 'owner';
  const members = useLoad(() => routes.organisationMembers(org.id).then((r) => r.members), [org.id]);
  const invitations = useLoad(() => (manager ? routes.invitations(org.id).then((r) => r.invitations) : Promise.resolve([] as CloudInvitation[])), [org.id, manager]);
  const [error, setError] = useState<string>();
  const [inviting, setInviting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [removingMember, setRemovingMember] = useState<{ userId: string; name: string }>();
  const billing = useLoad(() => (manager ? routes.billing(org.id) : Promise.resolve(undefined)), [org.id, manager]);
  const [buying, setBuying] = useState<'trial' | 'seats'>();
  // Inviting or withdrawing changes the seats held, so the billing figures follow.
  const afterInvitations = () => {
    invitations.reload();
    billing.reload();
  };

  const act = async (f: () => Promise<unknown>, after?: () => void) => {
    setError(undefined);
    try {
      await f();
      after?.();
    } catch (e) {
      setError(problemText(e));
    }
  };
  const leave = () => {
    setLeaving(false);
    void act(
      () => routes.removeMember(org.id, me),
      () => {
        void cloud.reload().then((next) => {
          const personal = next.organisations.find((o) => o.kind === 'personal') ?? next.organisations[0];
          if (personal) cloud.switchTo(personal.id);
          navigate('/workbenches', { replace: true });
        });
      },
    );
  };

  const list = members.data ?? [];
  const owners = list.filter((m) => m.role === 'owner').length;
  const managers = list.filter((m) => MANAGERS.has(m.role)).length;
  const open = invitations.data ?? [];
  const nameOf = (userId: string) => list.find((m) => m.userId === userId)?.name ?? list.find((m) => m.userId === userId)?.email;

  return (
    <Page>
      <PageHeader
        tone={tone}
        icon={<Persons />}
        title={org.name}
        subtitle={manager ? 'The people in this team, what each of them may do, and who has been invited.' : 'The people in this team and what each of them may do; its owners and admins invite people.'}
        actions={
          <>
            {manager && (
              <Button size="sm" variant="primary" onPress={() => setInviting(true)}>
                Invite people
              </Button>
            )}
            <Dropdown>
              <Button size="sm" variant="secondary" aria-label="More for this team">
                More
              </Button>
              <Dropdown.Popover placement="bottom end" className="min-w-48">
                <Dropdown.Menu aria-label="More for this team" onAction={(key) => (key === 'rename' ? setRenaming(true) : key === 'leave' ? setLeaving(true) : undefined)}>
                  {manager ? (
                    <Dropdown.Item id="rename" textValue="Rename team">
                      <Label>Rename team</Label>
                    </Dropdown.Item>
                  ) : null}
                  <Dropdown.Item id="leave" textValue="Leave team" variant="danger">
                    <Label>Leave team</Label>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          </>
        }
      />
      <div className="art-stats">
        <Stat
          tone={tone}
          icon={<Persons />}
          label="Members"
          value={members.data ? list.length : undefined}
          detail={members.data ? `${owners} ${owners === 1 ? 'owner' : 'owners'}${managers > owners ? ` · ${managers - owners} ${managers - owners === 1 ? 'admin' : 'admins'}` : ''}` : ''}
        />
        {manager ? (
          <Stat tone={tone} icon={<Envelope />} label="Invitations" value={invitations.data ? open.length : undefined} detail={open.length ? `Next ends ${day(open.map((i) => i.expiresAt).sort()[0]!)}` : 'None waiting'} />
        ) : (
          <Stat tone={tone} icon={<Shield />} label="You are" value={ROLE_TEXT[org.role]} detail="Owners change roles" />
        )}
        {billing.data ? (
          <Stat
            tone={tone}
            icon={<CreditCard />}
            label="Seats"
            value={billing.data.configured && !billing.data.operator ? `${billing.data.members + billing.data.openInvitations} of ${billing.data.seatLimit}` : billing.data.members + billing.data.openInvitations}
            detail={!billing.data.configured ? 'No seat limit' : billing.data.plan === 'teams' ? 'Teams' : 'Free plan: the owner only'}
            needsYou={billing.data.configured && !billing.data.operator && billing.data.plan === 'teams' && billing.data.members + billing.data.openInvitations >= billing.data.seatLimit}
          />
        ) : null}
      </div>
      {billing.data ? <BillingBanner billing={billing.data} owner={owner} onManage={() => void act(async () => window.location.assign((await routes.billingPortal(org.id, billing.data?.status === 'paused' || billing.data?.status === 'unpaid' ? 'payment_method_update' : undefined)).url))} /> : null}
      <ErrorNote error={error ?? members.error} />
      <ConfirmDialog isOpen={leaving} title={`Leave ${org.name}`} confirmLabel="Leave" danger onCancel={() => setLeaving(false)} onConfirm={leave}>
        Your desktops lose their access to it, and someone there has to invite you back.
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={!!removingMember}
        title={`Remove ${removingMember?.name ?? 'this person'} from ${org.name}`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemovingMember(undefined)}
        onConfirm={async () => {
          const m = removingMember;
          setRemovingMember(undefined);
          if (m) await act(() => routes.removeMember(org.id, m.userId), members.reload);
        }}
      >
        Their desktops lose their access to it.
      </ConfirmDialog>
      <ListGroup title="Members" count={members.data ? list.length : undefined} description={manager ? 'What each of them may do. Owners decide everything, admins manage people and the team’s workbenches, members use them.' : undefined}>
        {list.map((m) => (
          <MemberRow
            key={m.userId}
            member={m}
            isMe={m.userId === me}
            myRole={org.role}
            lastOwner={m.role === 'owner' && owners < 2}
            onRole={(role) => void act(() => routes.setMemberRole(org.id, m.userId, role), members.reload)}
            onRemove={() => setRemovingMember({ userId: m.userId, name: m.name ?? m.email ?? 'this person' })}
          />
        ))}
        {!members.data && !members.error ? <ListBlock className="text-sm text-muted">Reading the team…</ListBlock> : null}
      </ListGroup>
      {manager && (
        <ListGroup
          title="Invitations"
          count={open.length || undefined}
          description="Each link works once, for a week."
          empty={
            invitations.data && open.length === 0 ? (
              <ArtEmpty tone={tone} icon={<Envelope />} title="No one is waiting to join" compact>
                An invitation waits here until the person accepts it or it ends, a week after it was sent.
              </ArtEmpty>
            ) : undefined
          }
        >
          {open.map((i) => (
            <ListRow
              key={i.id}
              icon={<Envelope />}
              title={i.email}
              description={`${ROLE_TEXT[i.role]} · sent by ${nameOf(i.invitedBy) ?? i.invitedByName ?? 'a former member'} · ends ${day(i.expiresAt)}`}
              compactControl
              control={
                <Button size="sm" variant="ghost" onPress={() => void act(() => routes.withdrawInvitation(org.id, i.id), afterInvitations)}>
                  Withdraw
                </Button>
              }
            />
          ))}
        </ListGroup>
      )}
      {billing.data ? <BillingSection tone={tone} organisationId={org.id} billing={billing.data} owner={owner} onAddSeats={() => setBuying('seats')} onStartTrial={() => setBuying('trial')} /> : null}
      <InviteModal
        isOpen={inviting}
        onClose={() => setInviting(false)}
        organisationId={org.id}
        organisationName={org.name}
        onSent={afterInvitations}
        billing={billing.data}
        {...(owner ? { onNeedSeats: () => setBuying(billing.data?.plan === 'teams' ? 'seats' : 'trial') } : {})}
      />
      {billing.data && buying === 'trial' ? <TrialModal isOpen onClose={() => setBuying(undefined)} organisationId={org.id} billing={billing.data} /> : null}
      {billing.data && buying === 'seats' ? <SeatsModal isOpen onClose={() => setBuying(undefined)} organisationId={org.id} billing={billing.data} onChanged={billing.reload} /> : null}
      <RenameModal isOpen={renaming} onClose={() => setRenaming(false)} />
    </Page>
  );
}

function MemberRow({ member: m, isMe, myRole, lastOwner, onRole, onRemove }: { member: CloudMember; isMe: boolean; myRole: CloudRole; lastOwner: boolean; onRole: (r: CloudRole) => void; onRemove: () => void }) {
  const manager = MANAGERS.has(myRole);
  // Only an owner makes or unmakes an owner, and a team always keeps one; the API holds the same rules.
  const canChange = manager && (m.role !== 'owner' || myRole === 'owner') && !lastOwner;
  const canRemove = canChange && !isMe;
  const roles: CloudRole[] = myRole === 'owner' ? ['owner', 'admin', 'member', 'viewer'] : ['admin', 'member', 'viewer'];
  const who = m.name ?? m.email ?? 'this person';
  return (
    <ListRow
      icon={
        <span className="avatar size-8 shrink-0 text-xs" data-tone={toneFor(m.userId)} aria-hidden="true">
          {initials(m)}
        </span>
      }
      title={
        <>
          {m.name ?? m.email ?? 'Someone'}
          {isMe ? <span className="font-normal text-muted"> (you)</span> : null}
        </>
      }
      description={`${m.email && m.name ? `${m.email} · ` : ''}joined ${day(m.joinedAt)}`}
      {...(canChange
        ? {
            control: (
              <>
                <ChoiceSelect label={`Role of ${who}`} className="w-fit min-w-32 shrink-0" value={m.role} options={Object.fromEntries(roles.map((r) => [r, ROLE_TEXT[r]])) as Record<CloudRole, string>} onChange={onRole} />
                {canRemove ? <RowMenu label={`More for ${who}`} items={[{ id: 'remove', label: 'Remove from the team', danger: true }]} onAction={onRemove} /> : null}
              </>
            ),
          }
        : { value: ROLE_TEXT[m.role] })}
    />
  );
}

function InviteModal({ isOpen, onClose, organisationId, organisationName, onSent, billing, onNeedSeats }: { isOpen: boolean; onClose: () => void; organisationId: string; organisationName: string; onSent: () => void; billing?: CloudBilling; /** Absent for an admin: only the owners add seats or start Teams. */ onNeedSeats?: () => void }) {
  const [email, setEmail] = useState('');
  /** The team is full: the refusal's sentence, and the way to make room. */
  const [full, setFull] = useState<string>();
  const [role, setRole] = useState<'member' | 'admin' | 'viewer'>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<{ email: string; link: string }>();
  const [copied, setCopied] = useState(false);
  const close = () => {
    setEmail('');
    setRole('member');
    setError(undefined);
    setSent(undefined);
    setCopied(false);
    setFull(undefined);
    onClose();
  };
  const address = email.trim().toLowerCase();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);
  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const inv = await routes.invite(organisationId, { email: address, role });
      setSent({ email: inv.email, link: inv.link });
      onSent();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'seat_limit') setFull(problemText(e));
      else setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <FormModal
        isOpen={isOpen}
        onClose={close}
        title="Invitation sent"
        blurb={`We emailed ${sent.email} a link to join ${organisationName}. You can also send them this link yourself; it is shown only now.`}
        submitLabel="Done"
        onSubmit={close}
        footer={
          <Button variant="primary" onPress={close}>
            Done
          </Button>
        }
      >
        <div className="flex items-center gap-2">
          <input readOnly aria-label="Invitation link" className="mono min-w-0 flex-1 truncate rounded-lg border border-field-border bg-field-background px-3 py-1.5 text-xs" value={sent.link} onFocus={(e) => e.currentTarget.select()} />
          <Button
            size="sm"
            variant="secondary"
            onPress={() => {
              void navigator.clipboard?.writeText(sent.link).then(() => setCopied(true));
            }}
          >
            <Copy className="size-3.5" />
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </FormModal>
    );
  }
  if (full) {
    const teams = billing?.plan === 'teams';
    return (
      <FormModal
        isOpen={isOpen}
        onClose={close}
        title={teams ? 'Add a seat first' : 'Inviting people needs Teams'}
        blurb={full}
        submitLabel={teams ? 'Add seats' : billing?.canStartTrial === false ? 'Upgrade to Teams' : 'Start free trial'}
        canSubmit={!!billing?.configured && !!onNeedSeats}
        onSubmit={() => {
          close();
          onNeedSeats?.();
        }}
      >
        {billing ? <p className="text-sm text-muted">{billing.priceText}.</p> : null}
        {onNeedSeats ? null : <p className="text-sm text-muted">Ask one of the team’s owners to {teams ? 'add a seat' : 'start Teams'}.</p>}
      </FormModal>
    );
  }
  return (
    <FormModal
      isOpen={isOpen}
      onClose={close}
      title={`Invite people to ${organisationName}`}
      blurb="They get an email with a link that works for a week. They sign in with any account that uses this address."
      submitLabel="Send invitation"
      canSubmit={valid}
      busy={busy}
      error={error}
      onSubmit={() => void send()}
    >
      <TextField variant="secondary" type="email" value={email} onChange={setEmail} isRequired autoFocus>
        <Label>Email</Label>
        <Input placeholder="name@example.com" autoComplete="off" />
      </TextField>
      <div className="grid gap-1.5">
        <span className="text-sm font-medium">Role</span>
        <Segment aria-label="Role" size="sm" selectedKey={role} onSelectionChange={(k) => setRole(k === 'admin' ? 'admin' : k === 'viewer' ? 'viewer' : 'member')} className="w-fit">
          <Segment.Item id="member">Member</Segment.Item>
          <Segment.Item id="admin">Admin</Segment.Item>
          <Segment.Item id="viewer">Viewer</Segment.Item>
        </Segment>
        <span className="text-xs text-muted">{role === 'admin' ? 'Admins invite and manage people, and administer the team’s workbenches, policies and secrets.' : role === 'viewer' ? 'Viewers look at the team’s workbenches, projects and catalogue, and change nothing. They take a seat.' : 'Members use the team’s workbenches, projects and catalogue.'}</span>
      </div>
    </FormModal>
  );
}

function RenameModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const cloud = useCloud()!;
  const [name, setName] = useState(cloud.organisation.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.renameOrganisation(cloud.organisation.id, name.trim());
      await cloud.reload();
      onClose();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Rename team" submitLabel="Save" canSubmit={name.trim().length > 0 && name.trim() !== cloud.organisation.name} busy={busy} error={error} onSubmit={() => void save()}>
      <TextField variant="secondary" value={name} onChange={setName} isRequired autoFocus>
        <Label>Team name</Label>
        <Input maxLength={100} />
      </TextField>
    </FormModal>
  );
}
